/**
 * React Query hooks for lab results
 */
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  getMyLabResults,
  uploadMyLabReport,
  getLabResults,
  getLabResult,
  createLabResult,
  uploadLabReport,
  labResultsApi,
  LabResult,
  LabResultCreate
} from '../api/lab-results';
import { toast } from 'sonner';

// ==================== Query Keys ====================

export const labResultKeys = {
  all: ['lab-results'] as const,
  lists: () => [...labResultKeys.all, 'list'] as const,
  list: (patientId: number, startDate?: string, endDate?: string) => 
    [...labResultKeys.lists(), patientId, { startDate, endDate }] as const,
  myList: (startDate?: string, endDate?: string) =>
    [...labResultKeys.lists(), 'me', { startDate, endDate }] as const,
  details: () => [...labResultKeys.all, 'detail'] as const,
  detail: (id: number) => [...labResultKeys.details(), id] as const,
};

// ==================== Hooks ====================

/**
 * Hook to fetch lab results for current patient user
 */
export function useMyLabResults(
  startDate?: string,
  endDate?: string,
  options?: Omit<UseQueryOptions<LabResult[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: labResultKeys.myList(startDate, endDate),
    queryFn: () => getMyLabResults(startDate, endDate),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // ✅ FIX: Don't refetch when window gains focus
    refetchOnMount: false, // ✅ FIX: Don't refetch on component mount if data exists
    refetchInterval: false, // ✅ FIX: Disable automatic polling
    ...options
  });
}

/**
 * Hook to fetch lab results for a patient
 */
export function useLabResults(
  patientId: number,
  startDate?: string,
  endDate?: string,
  options?: Omit<UseQueryOptions<LabResult[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: labResultKeys.list(patientId, startDate, endDate),
    queryFn: () => getLabResults(patientId, startDate, endDate),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // ✅ FIX: Don't refetch when window gains focus
    refetchOnMount: false, // ✅ FIX: Don't refetch on component mount if data exists
    refetchInterval: false, // ✅ FIX: Disable automatic polling
    ...options
  });
}

/**
 * Hook to fetch a single lab result
 */
export function useLabResult(
  id: number,
  options?: Omit<UseQueryOptions<LabResult>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: labResultKeys.detail(id),
    queryFn: () => getLabResult(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
}

/**
 * Hook to create a lab result
 */
export function useCreateLabResult() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LabResultCreate) => createLabResult(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: labResultKeys.list(variables.patient_id) 
      });
      toast.success('Lab result recorded successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to record lab result');
    }
  });
}

/**
 * Hook to upload a lab report file for current patient
 */
export function useUploadMyLabReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      patient_id: number;
      test_type: string;
      test_date: string;
      file: File;
      notes?: string;
    }) => uploadMyLabReport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: labResultKeys.myList()
      });
      toast.success('Lab report uploaded successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload lab report');
    }
  });
}

/**
 * Hook to upload a lab report file (legacy - with patient_id)
 */
export function useUploadLabReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      patient_id: number;
      test_type: string;
      test_date: string;
      file: File;
      notes?: string;
    }) => uploadLabReport(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: labResultKeys.list(variables.patient_id) 
      });
      toast.success('Lab report uploaded successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload lab report');
    }
  });
}

/**
 * Hook to delete a lab result
 */
export function useDeleteLabResult() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patientId }: { id: number; patientId?: number }) => 
      labResultsApi.deleteLabResult(id),
    onSuccess: (_, variables) => {
      // Invalidate both patient-specific and 'me' queries
      if (variables.patientId) {
        queryClient.invalidateQueries({ 
          queryKey: labResultKeys.list(variables.patientId) 
        });
      }
      queryClient.invalidateQueries({
        queryKey: labResultKeys.myList()
      });
      toast.success('Lab result deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete lab result');
    }
  });
}